/**
 * Countdown arithmetic, separated from the React that drives it.
 *
 * Pure so it can be tested without a DOM. The React wiring around it is thin
 * but was not trivial to get right - the first version kept its anchor in a ref
 * and relied on a state update that React could legitimately skip, so the clock
 * never appeared at all.
 */

export interface Anchor {
  /** When the server's figure arrived, as a local timestamp. */
  readonly at: number;
  readonly remainingMs: number;
}

/**
 * Milliseconds left, from the last figure the server sent.
 *
 * Elapsed time is measured as a difference of wall-clock timestamps rather than
 * counted by an interval: a backgrounded tab stops firing timers, and a counted
 * clock would come back however many seconds wrong the phone was asleep.
 */
export function remainingFrom(anchor: Anchor | null, now: number): number | null {
  if (!anchor) return null;
  return Math.max(0, anchor.remainingMs - (now - anchor.at));
}

/** Whole seconds to show. Rounded UP, so a clock never shows 0 while time is left. */
export function secondsLeft(remainingMs: number): number {
  return Math.ceil(remainingMs / 1000);
}

export const DEFAULT_WARNING_MS = 20_000;

/** Inside the warning window? */
export function isWarning(remainingMs: number | null, warningMs: number | undefined): boolean {
  if (remainingMs === null) return false;
  return remainingMs <= (warningMs ?? DEFAULT_WARNING_MS);
}

/** How much of the turn is left, 0..1, for the ring. */
export function sweepFraction(remainingMs: number, limitMs: number): number {
  if (limitMs <= 0) return 0;
  return Math.max(0, Math.min(1, remainingMs / limitMs));
}

/** The last stretch of a turn that gets counted out loud. */
const TICK_WITHIN_SECONDS = 10;

/**
 * Whether the clock should tick, given the second it last ticked on.
 *
 * Pure so it can be tested without a clock, a component or a speaker. The rule
 * is the whole feature: inside the last ten seconds, once per whole second,
 * never twice for the same one.
 *
 * Zero and below are silent. The second a turn expires belongs to whatever
 * happens next - an auto-play, or the next player's clock starting - and a
 * tick there would land on top of it.
 */
export function tickDue(spoken: number | null, seconds: number): boolean {
  if (seconds <= 0 || seconds > TICK_WITHIN_SECONDS) return false;
  return spoken !== seconds;
}
